import React, { useEffect, useMemo, useState } from 'react';

import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import cx from 'classnames';
import { ASSIGNMENT_CREATE_MODES, MOCK_TEMPLATES } from 'constants/assignments';

import ClassSelector from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/ClassSelector';
import ExemplarUpload from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/ExemplarUpload';
import RubricSelector from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/RubricSelector';
import StandardSelector from 'pages/Teachers/Assignments/MathAssignmentCreateFlow/components/StandardSelector';
import {
  assignmentAssign,
  recentSectionsList
} from 'pages/Teachers/shared/AssignmentDialog/components/AssignAssignment/sdk';
import { VALUES_OPTIONS } from 'pages/Teachers/shared/AssignmentDialog/constants';
import { trackerCreate } from 'pages/Teachers/shared/AssignmentDialog/sdk';

import { ReactComponent as CheckIcon } from './assets/check-icon.svg';
import { ReactComponent as DocumentIcon } from './assets/document.svg';
import { ReactComponent as FilterIcon } from './assets/filter.svg';
import { ReactComponent as SearchIcon } from './assets/search.svg';
import styles from './styles.module.scss';

const MathAssignmentCreateFlow = ({ onAssignSuccess, onClose }) => {
  const [mode, setMode] = useState(ASSIGNMENT_CREATE_MODES.TEMPLATE);

  const [assignmentName, setAssignmentName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedRubric, setSelectedRubric] = useState(VALUES_OPTIONS[1]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [rawSections, setRawSections] = useState([]);
  const [isLoadingSections, setIsLoadingSections] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [showRubricDropdown, setShowRubricDropdown] = useState(false);

  const processRecentSections = ({ nestedData }) => {
    if (!Array.isArray(nestedData)) return [];

    const allSections = [];

    nestedData.forEach((subArray) => {
      if (Array.isArray(subArray)) {
        subArray.forEach((section) => {
          if (section) allSections.push(section);
        });
      }
    });

    const uniqueSections = Array.from(
      new Map(allSections.map((item) => [item.id, item])).values()
    );

    return uniqueSections;
  };

  const sections = useMemo(
    () => processRecentSections({ nestedData: rawSections }),
    [rawSections]
  );

  const addSection = ({ section }) => {
    if (!selectedSections.find((s) => s.id === section.id)) {
      setSelectedSections([...selectedSections, section]);
    }
  };

  const removeSection = ({ id }) => {
    setSelectedSections(selectedSections.filter((s) => s.id !== id));
  };

  const handleCreateAssignment = async () => {
    setIsSubmitting(true);

    try {
      let standardId = selectedStandards;

      if (typeof standardId === 'object' && standardId !== null) {
        standardId = standardId.id || '';
      }

      const standardIds = selectedStandards.map((standard) => standard.id);
      const objectivePayload = {
        name: 'Page 1',
        value_type: selectedRubric.key,
        max_value: 100,
        depth: 1,
        description: '',
        children: [],
        standard_ids: standardIds,
        isHeader: true,
        order: 0,
        disableEdit: false
      };

      const formData = new FormData();
      formData.append('name', assignmentName);
      formData.append('description', description);
      formData.append('objectives', JSON.stringify(objectivePayload));

      if (uploadedFile && uploadedFile.id) {
        formData.append(
          'attachment_file_ids',
          JSON.stringify([uploadedFile.id])
        );
      }

      const {
        success: createSuccess,
        data: createData,
        errors: createErrors
      } = await trackerCreate(formData);

      if (!createSuccess) {
        throw new Error(JSON.stringify(createErrors));
      }

      const trackerId = createData.id;
      const now = new Date();
      const due = new Date();
      due.setDate(now.getDate() + 2);

      const assignmentPayload = {
        sections_data: selectedSections.map((section) => ({
          section_id: section.id,
          open: now.toISOString(),
          due: due.toISOString()
        }))
      };

      const { success: assignSuccess, errors: assignErrors } =
        await assignmentAssign(trackerId, assignmentPayload);

      if (!assignSuccess) {
        throw new Error(JSON.stringify(assignErrors));
      }
      onAssignSuccess();
    } catch (error) {
      console.error('Workflow failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMode = () => {
    setMode((prev) =>
      prev === ASSIGNMENT_CREATE_MODES.TEMPLATE
        ? ASSIGNMENT_CREATE_MODES.SCRATCH
        : ASSIGNMENT_CREATE_MODES.TEMPLATE
    );
    setSelectedTemplate(null);
    setUploadedFile(null);
  };

  const handleSelectTemplate = ({ template }) => {
    setSelectedTemplate(template);
    setAssignmentName(template.name);
    setShowTemplateDropdown(false);
  };

  useEffect(() => {
    const fetchSections = async () => {
      setIsLoadingSections(true);
      try {
        const response = await recentSectionsList();

        const actualData = Array.isArray(response)
          ? response
          : response?.data || [];

        setRawSections(actualData);
      } catch (err) {
        console.error('Failed to fetch sections', err);
        setRawSections([]);
      } finally {
        setIsLoadingSections(false);
      }
    };
    fetchSections();
  }, []);

  const removeStandard = ({ id }) => {
    setSelectedStandards((prev) => prev.filter((s) => s.id !== id));
  };

  const addStandard = ({ standard }) => {
    if (!selectedStandards.find((s) => s.id === standard.id)) {
      setSelectedStandards((prev) => [...prev, standard]);
    }
  };

  // TODO: add real form validation
  const isFormValid = assignmentName.trim() !== '' && selectedSections !== '';

  // TODO: Add scrolling for tablet devices or when templates are too much and test on different devices.§
  return (
    <div className={styles.modalWrapper}>
      {/* --- FLOATING TEMPLATE LIST (Only in Template Mode) --- */}
      {showTemplateDropdown && mode === ASSIGNMENT_CREATE_MODES.TEMPLATE && (
        <div className={styles.templateFloatingList}>
          {MOCK_TEMPLATES.map((template) => {
            const isSelected = selectedTemplate?.id === template.id;
            return (
              <div
                key={template.id}
                className={cx(styles.templateItem, {
                  [styles.selected]: isSelected
                })}
                onClick={() => handleSelectTemplate({ template: template })}
              >
                <div className={styles.checkWrapper}>
                  {isSelected && <CheckIcon className={styles.checkIcon} />}
                </div>

                <div className={styles.itemContent}>
                  <div className={styles.templateItemName}>{template.name}</div>
                  <div className={styles.templateItemMeta}>
                    <span className={styles.tag}>{template.grade}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={styles.container}>
        {/* --- HEADER --- */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h3 className={styles.title}>Launch New Assignment</h3>
          </div>

          <div className={styles.assignmentTypeRow}>
            <div className={styles.typeLabel}>
              <DocumentIcon />
              <span>Assignment</span>
            </div>
            <button onClick={toggleMode} className={styles.modeToggle}>
              {mode === ASSIGNMENT_CREATE_MODES.TEMPLATE
                ? 'Create from scratch →'
                : 'Use a Template →'}
            </button>
          </div>
        </div>

        {/* --- FORM CONTENT --- */}
        <div className={styles.formContent}>
          {/* Template Search (Template Mode Only) */}
          {mode === ASSIGNMENT_CREATE_MODES.TEMPLATE && (
            <div
              className={styles.searchWrapper}
              onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
            >
              <FilterIcon />
              <input
                type="text"
                readOnly
                placeholder="Select a Template..."
                value={selectedTemplate?.name || ''}
                className={styles.templateInput}
              />
              <SearchIcon className={styles.searchIcon} />
            </div>
          )}

          <input
            type="text"
            className={styles.inputField}
            placeholder="Assignment Name"
            value={assignmentName}
            onChange={(e) => setAssignmentName(e.target.value)}
          />

          <textarea
            className={cx(styles.inputField, styles.textArea)}
            placeholder="Add a Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />

          {/* Learning Standard (Scratch Mode Only) */}
          {mode === ASSIGNMENT_CREATE_MODES.SCRATCH && (
            <StandardSelector
              selectedStandards={selectedStandards}
              onAdd={addStandard}
              onRemove={removeStandard}
            />
          )}

          <ClassSelector
            sections={sections}
            selectedSections={selectedSections}
            onAdd={addSection}
            onRemove={removeSection}
            isLoading={isLoadingSections}
          />

          <RubricSelector
            options={VALUES_OPTIONS}
            selected={selectedRubric}
            onSelect={setSelectedRubric}
            isOpen={showRubricDropdown}
            onToggle={() => setShowRubricDropdown(!showRubricDropdown)}
          />

          {mode === ASSIGNMENT_CREATE_MODES.SCRATCH && (
            <ExemplarUpload file={uploadedFile} setFile={setUploadedFile} />
          )}
        </div>

        {/* --- FOOTER --- */}
        <div className={styles.footer}>
          <button
            className={styles.createButton}
            disabled={!isFormValid || isSubmitting}
            onClick={handleCreateAssignment}
          >
            {isSubmitting ? (
              'Processing...'
            ) : (
              <>
                <span></span>
                CREATE ASSIGNMENT
                <ArrowForwardIcon />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MathAssignmentCreateFlow;
